import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

type CollectionSnapshot<T> = {
  value: T;
  windowY: number;
  contentX: number;
  contentY: number;
};
const CollectionMemory = createContext<Map<string, CollectionSnapshot<unknown>> | null>(null);

/** Mount inside the authenticated shell; unmounting clears all transient collection context. */
export function CollectionNavigationProvider({ children }: PropsWithChildren) {
  const [memory] = useState(() => new Map<string, CollectionSnapshot<unknown>>());
  return <CollectionMemory.Provider value={memory}>{children}</CollectionMemory.Provider>;
}

/** Keep controls and position together, without persisting search text or caching records. */
export function useCollectionNavigation<T>(
  key: string,
  initialValue: () => T,
  { restoreWindow = true } = {},
) {
  const memory = useContext(CollectionMemory);
  const initial = useRef(memory?.get(key) as CollectionSnapshot<T> | undefined);
  const [value, setValue] = useState(() => initial.current?.value ?? initialValue());
  const latest = useRef(value);
  latest.current = value;
  const rootRef = useRef<HTMLElement>(null);
  const position = useRef({
    windowY: initial.current?.windowY ?? 0,
    contentX: initial.current?.contentX ?? 0,
    contentY: initial.current?.contentY ?? 0,
  });
  const remember = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const content = root.querySelector<HTMLElement>('[data-collection-scroll]');
    position.current = {
      windowY: restoreWindow ? window.scrollY : 0,
      contentX: content?.scrollLeft ?? position.current.contentX,
      contentY: content?.scrollTop ?? position.current.contentY,
    };
    memory?.set(key, { value: latest.current, ...position.current });
  }, [key, memory, restoreWindow]);

  useLayoutEffect(() => {
    let restoring = true;
    const restore = () => {
      if (restoreWindow) window.scrollTo(0, initial.current?.windowY ?? 0);
      const content = rootRef.current?.querySelector<HTMLElement>('[data-collection-scroll]');
      if (content) {
        content.scrollLeft = initial.current?.contentX ?? 0;
        content.scrollTop = initial.current?.contentY ?? 0;
      }
    };
    restore();
    // A second layout turn handles browser Back restoration and newly mounted collection content.
    const frame = requestAnimationFrame(() => {
      restore();
      restoring = false;
    });
    const scrolled = () => {
      if (!restoring) remember();
    };
    window.addEventListener('scroll', scrolled, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scrolled);
      // Do not sample scroll here: the next route may already have changed page height.
    };
  }, [remember, restoreWindow]);

  useLayoutEffect(() => {
    memory?.set(key, { value, ...position.current });
  }, [key, memory, value]);

  return { value, setValue, rootRef, remember };
}
